import React, {
  createContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { createLinkText, normalizeChatHistory } from "../utils/common";
import { v4 as uuidv4 } from "uuid";

/**
 * Initial state for the ChatBot context.
 * @type {Object}
 * @property {number} resetSocket - Counter or flag to trigger socket reset.
 * @property {Object} receivedQuestionType - Type and details of the received question.
 * @property {string} userLoginID - ID of the logged-in user.
 * @property {string} connectedShortCode - Short code for the connected bot.
 * @property {string} connectedBotID - ID of the connected bot.
 * @property {string} connectedDomainURL - Domain URL where the bot is connected.
 * @property {string} connectedDomainPath - Domain path where the bot is connected.
 * @property {WebSocket|null} globalSocket - The main WebSocket connection.
 * @property {Array} messageList - List of all messages in the chat.
 * @property {Array} currentSessionMessageList - List of messages in the current session.
 * @property {Object|null} receivedMessage - The latest received message object.
 * @property {boolean} responseDelay - Flag indicating if there is a response delay.
 * @property {string} chatHistoryConsent - User consent status for chat history.
 * @property {string} trustedDeviceConsent - User consent status for trusted device.
 * @property {string} env - Environment (e.g., 'dev', 'prod').
 * @property {string} connectionId - Unique ID for the connection.
 * @property {string|null} ccSocket - WebSocket URL or object for customer care (CC).
 * @property {string} ccStatus - Status of the customer care connection.
 * @property {string} botAction - Current action state of the bot (e.g., 'bot').
 * @property {boolean|null} is_chat_end - Flag indicating if the chat has ended.
 * @property {string|null} botUserId - ID of the bot user.
 * @property {string|null} sessionId - Current session ID.
 * @property {boolean} isChatHistoryFetched - Flag indicating if chat history has been fetched.
 * @property {boolean} isSensitiveDataTrigger - Flag for sensitive data trigger.
 * @property {boolean} isSensitiveDataTriggerInSession - Flag for sensitive data trigger within session.
 * @property {boolean} isDeleteAccountTrigger - Flag for delete account trigger.
 * @property {boolean} isPullHistoryTrigger - Flag for pull history trigger.
 * @property {boolean} initialLoader - Flag for pull history trigger.
 * @property {string} istmsLink_value - Terms link value.
 * @property {string} isppLink_value - Privacy policy link value.
 * @property {string} chatLinkColor - Color for chat links.
 * @property {string} defaultLanguage - Default language for the chat.
 */
const initialAuthState = {
  resetSocket: 0,
  receivedQuestionType: { type: "", id: 1, question: "" },
  userLoginID: "",
  connectedShortCode: "",
  connectedBotID: "",
  connectedDomainURL: "",
  connectedDomainPath: "",
  globalSocket: null,
  messageList: [],
  currentSessionMessageList: [],
  receivedMessage: null,
  responseDelay: false,
  chatHistoryConsent: "",
  trustedDeviceConsent: "",
  env: "",
  connectionId: "",
  ccSocket: null,
  ccStatus: "",
  botAction: "bot",
  is_chat_end: null,
  botUserId: null,
  sessionId: null,
  isChatHistoryFetched: false,
  isSensitiveDataTrigger: false,
  isSensitiveDataTriggerInSession: false,
  isDeleteAccountTrigger: false,
  isPullHistoryTrigger: false,
  istmsLink_value: "",
  isppLink_value: "",
  chatLinkColor: "blue",
  defaultLanguage: "en",
  initialLoader: false,
};

const getMessageFileData = (message = {}) =>
  message.file_data || message.uploaded_file || "";

const normalizeMessageEntry = (message = {}) => ({
  ...message,
  file_data: getMessageFileData(message),
});

const normalizeMessageList = (messages = []) => {
  if (!Array.isArray(messages)) return messages;

  const hasNestedHistoryShape = messages.some(
    (message) => message?.bot || message?.user,
  );

  if (hasNestedHistoryShape) {
    return normalizeChatHistory(messages).map(normalizeMessageEntry);
  }

  return messages.map(normalizeMessageEntry);
};

/**
 * Reducer function to manage ChatBot state.
 * @param {Object} state - Current state.
 * @param {Object} action - Action object containing type and payload.
 * @returns {Object} New state.
 */
const reducer = (state, action) => {
  switch (action.type) {
    case "GLOBAL_SOCKET_CONNECTION": {
      return {
        ...state,
        globalSocket: action.payload,
      };
    }

    case "MSG_LIST_UPDATE": {
      return {
        ...state,
        messageList: normalizeMessageList(action.payload),
      };
    }

    case "CURRENT_SESSION_MSG_LIST_UPDATE": {
      return {
        ...state,
        currentSessionMessageList: normalizeMessageList(action.payload),
      };
    }

    case "RECEIVED_MSG_UPDATE": {
      if (!action.payload) return { ...state, receivedMessage: null };

      const msg = action.payload;
      const chatLinkColor = state.chatLinkColor;
      const laMsgList = [...state.messageList];
      const laCurrentSessionMsgList = [...state.currentSessionMessageList];
      const fileData = getMessageFileData(msg);

      // Extract and update Session ID if available
      const incomingSessionId = msg.sessionId || msg.session_id;
      let newSessionId = state.sessionId;
      if (incomingSessionId && !state.sessionId) {
        newSessionId = incomingSessionId;
      }

      // Logic to append messages based on historical data structure
      if (msg.user_message) {
        // Handle user input part (always 'send' direction)
        const userEntry = {
          ...msg,
          file_data: fileData,
          question: createLinkText(msg.user_message, chatLinkColor),
          directionType: "send",
        };
        laMsgList.push(userEntry);
        laCurrentSessionMsgList.push({
          ...userEntry,
          question: msg.user_message,
        });
      } else {
        // Handle bot/cc/system reply part
        const receivedMsg = msg.question || msg.cc_reply || msg.message;
        if (receivedMsg || fileData) {
          const botEntry = {
            ...msg,
            file_data: fileData,
            question: createLinkText(receivedMsg, chatLinkColor),
            directionType: msg.directionType || "receive",
          };
          laMsgList.push(botEntry);
          laCurrentSessionMsgList.push({ ...botEntry, question: receivedMsg });
        }
      }

      return {
        ...state,
        sessionId: newSessionId,
        messageList: laMsgList,
        currentSessionMessageList: laCurrentSessionMsgList,
        receivedMessage: null, // processed immediately
      };
    }
    case "RECEIVED_QUESTION_TYPE": {
      return {
        ...state,
        receivedQuestionType: action?.payload,
      };
    }
    case "SENSITIVE_DATA_TRIGGER": {
      return {
        ...state,
        isSensitiveDataTrigger: action?.payload,
      };
    }

    case "SENSITIVE_DATA_TRIGGER_IN_SESSION": {
      return {
        ...state,
        isSensitiveDataTriggerInSession: action?.payload,
      };
    }

    case "DELETE_ACCOUNT_TRIGGER": {
      return {
        ...state,
        isDeleteAccountTrigger: action?.payload,
      };
    }

    case "PULL_HISTORY_TRIGGER": {
      return {
        ...state,
        isPullHistoryTrigger: action?.payload,
      };
    }

    case "SET_CONNECTED_PARAMETERS": {
      return {
        ...state,
        connectedShortCode: action.payload.shortCode,
        connectedDomainURL: action.payload.domainURL,
        connectedDomainPath: action.payload.domainPath,
        connectedBotID: action.payload.BotID,
        env: action.payload.env,
        userLoginID: action.payload.userLoginID,
        botUserId: action.payload.botUserId,
        istmsLink_value: action.payload.istmsLink_value,
        isppLink_value: action.payload.isppLink_value,
      };
    }
    case "SET_CHAT_CONSENT": {
      return {
        ...state,
        chatHistoryConsent: action?.payload,
      };
    }

    case "SET_CHAT_LANGUAGE": {
      return {
        ...state,
        defaultLanguage: action.payload,
      };
    }
    case "SET_USER_LOGIN": {
      return {
        ...state,
        userLoginID: action?.payload,
      };
    }
    case "SET_TRUSTED_DEVICE": {
      return {
        ...state,
        trustedDeviceConsent: action?.payload,
      };
    }

    case "DELAY_UPDATE": {
      return {
        ...state,
        responseDelay: action.payload,
      };
    }

    case "UPDATE_CONNECTION_ID": {
      return {
        ...state,
        connectionId: action.payload,
      };
    }

    case "UPDATE_CC_SOCKET": {
      return {
        ...state,
        ccSocket: action.payload,
      };
    }

    case "UPDATE_BOT_ACTION": {
      return {
        ...state,
        botAction: action.payload,
      };
    }

    case "UPDATE_CC_STATUS": {
      return {
        ...state,
        ccStatus: action.payload,
      };
    }

    case "UPDATE_CHAT_END": {
      return {
        ...state,
        is_chat_end: action.payload,
      };
    }

    case "UPDATE_SESSION_ID": {
      return {
        ...state,
        sessionId: action.payload,
      };
    }

    case "IS_CHAT_HISTORY_FETCHED": {
      return {
        ...state,
        isChatHistoryFetched: action.payload,
      };
    }

    case "UPDATE_RESET_SOCKET": {
      return {
        ...state,
        resetSocket: action.payload,
      };
    }

    case "UPDATE_CHAT_LINK_COLOR": {
      return {
        ...state,
        chatLinkColor: action.payload,
      };
    }

    default: {
      return { ...state };
    }
  }
};

/**
 * Context for the ChatBot, providing state and actions.
 */
const ChatBotContext = createContext({
  ...initialAuthState,
  updateDelay: () => Promise.resolve(),
  updateConnectionID: () => Promise.resolve(),
  updateCCSocket: () => Promise.resolve(),
  updateCCStatus: () => Promise.resolve(),
  updateChatEnd: () => Promise.resolve(),
  updateSessionId: () => Promise.resolve(),
  chatHistoryFetchedStatus: () => Promise.resolve(),
  updateResetSocket: () => Promise.resolve(),
  updateChatLinkColor: () => Promise.resolve(),
  setGlobalSocket: () => Promise.resolve(),
  updateReceivedMsg: () => Promise.resolve(),
  updateMsgList: () => Promise.resolve(),
  currentSessionUpdateMsgList: () => Promise.resolve(),
  updateConnectedShortCode: () => Promise.resolve(),
  updateBotAction: () => Promise.resolve(),
  updateUserLogin: () => Promise.resolve(),
  handleSetChatConsent: () => Promise.resolve(),
  updateReceivedQuestionType: () => Promise.resolve(),
  updateIsSensitiveTrigger: () => Promise.resolve(),
  updateIsSensitiveTriggerInSession: () => Promise.resolve(),
  updateIsDeleteAccountTrigger: () => Promise.resolve(),
  updateIsPullHistoryTrigger: () => Promise.resolve(),
  setLanguage: () => Promise.resolve(),
});

/**
 * Provider component for the ChatBot Context.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The ChatBotContext Provider.
 */

const location = {
  pathname: window.location.pathname,
  href: window.location.href,
};

const buildSocketURL = (baseURL, params = {}) => {
  const url = new URL(baseURL);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
};

export const ChatBotProvider = ({ children, botId }) => {
  const [initialLoader, setInitialLoader] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const sessionResumedRef = useRef(false);
  const currentBot = botId || "";

  useEffect(() => {
    console.log(currentBot, "currentBot-useEffect");
    if (currentBot) {
      console.log(currentBot, "currentBot-useEffect-exists");
      if (
        (((!!state.connectedShortCode &&
          !!state.connectedDomainURL &&
          !!state.connectedDomainPath) ||
          !!state.connectedBotID) &&
          !!state.connectionId) ||
        state.ccSocket
      ) {
        setInitialLoader(true);
        let ws = "";
        let socketURL = "";
        if (state.ccSocket) {
          socketURL = state.ccSocket;
        } else if (!!state.connectedBotID && !state.connectedShortCode) {
          socketURL = buildSocketURL(
            `${process.env.REACT_APP_TEST_SOCKET_URL}${state.connectedBotID}/`,
            {
              env: state.env,
              botUserId: state.botUserId,
              istmsLink_value: state.istmsLink_value,
              isppLink_value: state.isppLink_value,
            },
          );
        } else if (!!state.isChatHistoryFetched) {
          socketURL = buildSocketURL(
            `${process.env.REACT_APP_EXTERNAL_SOCKET_URL}${state.connectedShortCode}/`,
            {
              domain: state.connectedDomainURL,
              path: state.connectedDomainPath,
              env: state.env,
              botUserId: state.botUserId,
              istmsLink_value: state.istmsLink_value,
              isppLink_value: state.isppLink_value,
            },
          );
        }

        if (!socketURL) {
          setInitialLoader(false);
          return undefined;
        }

        let browser_unique_id = localStorage.getItem("browser_unique_id");
        let session_id = uuidv4();

        if (!browser_unique_id) {
          browser_unique_id = uuidv4();
          localStorage.setItem("browser_unique_id", browser_unique_id);
        }

        if (!!state.userLoginID) {
          socketURL = buildSocketURL(socketURL, {
            user_id: state.userLoginID,
          });
        } else if (!state.userLoginID) {
          socketURL = buildSocketURL(socketURL, { user_id: "null" });
        }

        socketURL = buildSocketURL(socketURL, {
          browser_unique_id,
          session_id,
          botUserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        if (location.pathname == "/bot-preview") {
          socketURL = buildSocketURL(socketURL, { preview: true });
        }

        socketURL = buildSocketURL(socketURL, { bot_id: currentBot });

        if (!!socketURL) {
          updateIsSensitiveTrigger(false);
          updateIsSensitiveTriggerInSession(false);
          updateIsDeleteAccountTrigger(false);
          updateIsPullHistoryTrigger(false);

          // localStorage.removeItem("saveChatHistoryObj")
          localStorage.removeItem("otpSendBody");
          localStorage.removeItem("tiggeredLoginUser");
        }

        ws = new WebSocket(socketURL);

        ws.onopen = () => {
          console.log("WebSocket connected");
        };
        setGlobalSocket(ws);

        ws.onmessage = (data) => {
          let socketData = JSON.parse(data.data);

          setInitialLoader(false);

          if (socketData?.message?.session_resumed) {
            sessionResumedRef.current = true;
            updateCCStatus("");
            updateBotAction("bot");
            updateDelay(false);
            return;
          }

          if (socketData?.detected_language) {
            //   console.log("language detected", socketData?.detected_language);
            if (socketData?.detected_language) {
              setLanguage(socketData?.detected_language);
            }
          }
          const ccStatus =
            socketData?.message?.bot?.cc_status ||
            socketData?.message?.cc_status ||
            "";
          updateCCStatus(ccStatus);
          if (
            socketData?.message?.is_chat_end ||
            socketData?.is_chat_end ||
            socketData?.message?.bot?.is_chat_end
          ) {
            sessionResumedRef.current = false;
            const chatEndMessage =
              typeof socketData?.message === "string"
                ? socketData.message
                : socketData?.message?.message ||
                  socketData?.message?.cc_reply ||
                  socketData?.message?.question ||
                  socketData?.message?.bot?.question ||
                  "";
            if (chatEndMessage) {
              updateDelay(false);
              updateReceivedMsg({
                message: chatEndMessage,
                directionType: "receive",
                isAnswered: false,
                sessionId: socketData?.session_id,
              });
              updateReceivedQuestionType({
                type: "",
                id: "",
                question: chatEndMessage,
              });
            }
            updateChatEnd(true);
          } else {
            if (ccStatus !== "connecting") {
              updateDelay(false);
            }
            const isDirectMessage =
              !socketData?.sender &&
              !socketData?.message?.bot &&
              (socketData?.message_type ||
                socketData?.sender_type ||
                typeof socketData?.message === "string");

            if (socketData.sender == "cc") {
              sessionResumedRef.current = false;
              updateReceivedMsg({
                ...socketData.message,
                directionType: "receive",
                isAnswered: false,
              });
            } else if (socketData.sender == "user") {
              sessionResumedRef.current = false;
              updateReceivedMsg({
                ...socketData.message,
                directionType: "receive",
                isAnswered: false,
              });
            } else if (socketData.sender == "system") {
              sessionResumedRef.current = false;
              updateReceivedMsg({
                ...socketData.message,
                directionType: "receive",
                isAnswered: false,
              });
            } else if (!!socketData.message?.bot) {
              sessionResumedRef.current = false;
              if (!socketData?.message?.bot?.cc_status) {
                updateBotAction("bot");
              }
              updateReceivedMsg({
                ...socketData.message?.bot,
                directionType: "receive",
                isAnswered: false,
                sessionId: socketData?.session_id,
              });
            } else if (isDirectMessage) {
              const isResumedExecutiveNotice =
                sessionResumedRef.current &&
                socketData?.sender_type === "executive";

              updateReceivedMsg({
                ...socketData,
                question: socketData?.message,
                message: socketData?.message,
                action: isResumedExecutiveNotice
                  ? ""
                  : socketData?.action ||
                    (socketData?.sender_type === "executive"
                      ? "human_takeover"
                      : ""),
                directionType: "receive",
                isAnswered: false,
                created_at: socketData?.datetime,
                datetime: socketData?.datetime,
              });
              updateReceivedQuestionType({
                type: socketData?.message_type || "",
                id: socketData?.id || "",
                question: socketData?.message || "",
              });
              sessionResumedRef.current = false;
            } else if (socketData.message?.file_data) {
              sessionResumedRef.current = false;
              updateReceivedMsg({
                ...socketData.message,
                directionType: "receive",
                isAnswered: false,
                datetime: socketData.datetime,
              });
            } else if (socketData.message?.user_message) {
              sessionResumedRef.current = false;
              updateReceivedMsg({
                ...socketData.message,
                directionType: "receive",
                isAnswered: true,
                datetime: socketData.datetime,
              });
            }
            if (socketData.message?.bot) {
              updateReceivedQuestionType({
                type: socketData.message?.bot?.type,
                id: socketData.message?.bot?.id,
                question: socketData.message?.bot?.question,
              });
            } else if (!isDirectMessage) {
              updateReceivedQuestionType({
                type: "",
                id: "",
                question:
                  socketData.sender == "cc"
                    ? socketData?.message?.cc_reply
                    : state.receivedQuestionType.question,
              });
            }
            if (
              !!socketData?.message?.bot?.is_sensitive ||
              !!socketData?.message?.is_sensitive
            ) {
              updateIsSensitiveTrigger(true);
            }
            if (
              !!socketData?.message?.bot?.chat_history_pull_request ||
              !!socketData?.message?.chat_history_pull_request
            ) {
              updateIsPullHistoryTrigger(true);
            }
            if (
              !!socketData?.message?.bot?.is_delete_account ||
              !!socketData?.message?.is_delete_account
            ) {
              updateIsDeleteAccountTrigger(true);
            }
          }
        };

        // ws.send(JSON.stringify({"message":{"id":"1","user_message":"hi"}}))

        // ws.onclose = () => {
        //     console.log('WebSocket disconnected');
        // };

        // ws.onerror = (error) => {
        //     console.error('WebSocket error:', error);
        // };

        // ws.receive = (data) => {
        //     console.log('WebSocket Receive', data);
        // };

        return () => {
          ws.close();
        };
      }
    }
  }, [
    state.connectedShortCode,
    state.connectedDomainURL,
    state.connectedDomainPath,
    state.connectedBotID,
    state.connectionId,
    state.ccSocket,
    state.userLoginID,
    state.isChatHistoryFetched,
    state.chatHistoryFetchedStatus,
    state.resetSocket,
    currentBot,
  ]);

  // Message list logic moved to reducer to handle rapid history dispatches atomically

  /**
   * Updates the response delay state.
   * @param {boolean} value - The new delay state.
   */
  const updateDelay = (value) => {
    dispatch({ type: "DELAY_UPDATE", payload: value });
  };

  /**
   * Updates the connection ID.
   * @param {string} value - The new connection ID.
   */
  const updateConnectionID = (value) => {
    dispatch({ type: "UPDATE_CONNECTION_ID", payload: value });
  };

  /**
   * Updates the Customer Care (CC) socket URL.
   * @param {string} value - The new CC socket URL.
   */
  const updateCCSocket = (value) => {
    dispatch({ type: "UPDATE_CC_SOCKET", payload: value });
  };

  /**
   * Updates the Customer Care (CC) status.
   * @param {string} value - The new CC status.
   */
  const updateCCStatus = (value) => {
    dispatch({ type: "UPDATE_CC_STATUS", payload: value });
  };

  /**
   * Updates the chat end status.
   * @param {boolean} value - True if chat has ended, false otherwise.
   */
  const updateChatEnd = (value) => {
    dispatch({ type: "UPDATE_CHAT_END", payload: value });
  };

  /**
   * Updates the session ID.
   * @param {string} value - The new session ID.
   */
  const updateSessionId = (value) => {
    dispatch({ type: "UPDATE_SESSION_ID", payload: value });
  };

  /**
   * Updates the chat history fetched status.
   * @param {boolean} value - True if history is fetched, false otherwise.
   */
  const chatHistoryFetchedStatus = (value) => {
    dispatch({ type: "IS_CHAT_HISTORY_FETCHED", payload: value });
  };

  /**
   * Updates the reset socket counter/flag.
   * @param {number} value - The new value.
   */
  const updateResetSocket = (value) => {
    dispatch({ type: "UPDATE_RESET_SOCKET", payload: value });
  };

  /**
   * Updates the chat link color.
   * @param {string} value - The new color string.
   */
  const updateChatLinkColor = (value) => {
    dispatch({ type: "UPDATE_CHAT_LINK_COLOR", payload: value });
  };

  /**
   * Updates the received message state.
   * @param {Object|null} value - The received message object.
   */
  const updateReceivedMsg = (value) => {
    dispatch({ type: "RECEIVED_MSG_UPDATE", payload: value });
  };

  /**
   * Updates the received question type.
   * @param {Object} value - Object containing type, id, and question.
   */
  const updateReceivedQuestionType = (value) => {
    dispatch({ type: "RECEIVED_QUESTION_TYPE", payload: value });
  };

  /**
   * Updates the sensitive data trigger flag.
   * @param {boolean} value - The new flag value.
   */
  const updateIsSensitiveTrigger = (value) => {
    dispatch({ type: "SENSITIVE_DATA_TRIGGER", payload: value });
  };

  /**
   * Updates the sensitive data trigger in session flag.
   * @param {boolean} value - The new flag value.
   */
  const updateIsSensitiveTriggerInSession = (value) => {
    dispatch({ type: "SENSITIVE_DATA_TRIGGER_IN_SESSION", payload: value });
  };

  /**
   * Updates the delete account trigger flag.
   * @param {boolean} value - The new flag value.
   */
  const updateIsDeleteAccountTrigger = (value) => {
    dispatch({ type: "DELETE_ACCOUNT_TRIGGER", payload: value });
  };

  /**
   * Updates the pull history trigger flag.
   * @param {boolean} value - The new flag value.
   */
  const updateIsPullHistoryTrigger = (value) => {
    dispatch({ type: "PULL_HISTORY_TRIGGER", payload: value });
  };

  /**
   * Updates the message list.
   * @param {Array} value - The new list of messages.
   */
  const updateMsgList = (value) => {
    dispatch({ type: "MSG_LIST_UPDATE", payload: value });
  };

  /**
   * Updates the current session message list.
   * @param {Array} value - The new list of messages for the current session.
   */
  const currentSessionUpdateMsgList = (value) => {
    dispatch({ type: "CURRENT_SESSION_MSG_LIST_UPDATE", payload: value });
  };

  /**
   * Sets the global WebSocket connection.
   * @param {WebSocket} value - The WebSocket instance.
   */
  const setGlobalSocket = (value) => {
    dispatch({ type: "GLOBAL_SOCKET_CONNECTION", payload: value });
  };

  /**
   * Updates the connected bot parameters (shortCode, domainURL, etc.).
   * @param {Object} value - Object containing connection parameters.
   */
  const updateConnectedShortCode = (value) => {
    dispatch({ type: "SET_CONNECTED_PARAMETERS", payload: value });
  };

  /**
   * Handles setting chat consents (history or trusted device).
   * @param {string} type - The type of consent ('chatHistory' or 'trustedDevice').
   * @param {string} value - The consent value.
   */
  const handleSetChatConsent = (type, value) => {
    if (type == "chatHistory") {
      dispatch({ type: "SET_CHAT_CONSENT", payload: value });
    } else if (type == "trustedDevice") {
      dispatch({ type: "SET_TRUSTED_DEVICE", payload: value });
    }
  };

  /**
   * Updates the bot action state.
   * @param {string} value - The new bot action.
   */
  const updateBotAction = (value) => {
    dispatch({ type: "UPDATE_BOT_ACTION", payload: value });
  };

  /**
   * Updates the user login ID.
   * @param {string} value - The user login ID.
   */
  const updateUserLogin = (value) => {
    dispatch({ type: "SET_USER_LOGIN", payload: value });
  };

  /**
   * Sets the default language for the chat.
   * @param {string} value - The language code (e.g., 'en').
   */
  const setLanguage = (value) => {
    // SET_CHAT_LANGUAGE
    dispatch({ type: "SET_CHAT_LANGUAGE", payload: value });
  };

  return (
    <ChatBotContext.Provider
      value={{
        ...state,
        updateDelay,
        updateConnectionID,
        updateCCSocket,
        updateCCStatus,
        updateChatEnd,
        updateSessionId,
        chatHistoryFetchedStatus,
        updateResetSocket,
        updateChatLinkColor,
        setGlobalSocket,
        updateReceivedMsg,
        updateMsgList,
        currentSessionUpdateMsgList,
        updateConnectedShortCode,
        handleSetChatConsent,
        updateReceivedQuestionType,
        updateBotAction,
        updateUserLogin,
        updateIsSensitiveTrigger,
        updateIsSensitiveTriggerInSession,
        updateIsDeleteAccountTrigger,
        updateIsPullHistoryTrigger,
        setLanguage,
        initialLoader,
      }}
    >
      {children}
    </ChatBotContext.Provider>
  );
};

export default ChatBotContext;
